/**
 * Styled primitives shared by the DFC Fitness Village surfaces (the village page and the
 * components that compose it). Each component still owns its own `style.ts`; only the
 * pieces the design repeats verbatim across them live here, so an eyebrow or a pill is
 * one definition, not five.
 *
 * Two rules hold for everything in the village theme:
 *  1. No colour literal. Everything arrives via a `DfcSurface` (src/Utils/dfcSurface.ts)
 *     or `DFC_PALETTE`.
 *  2. Every control is keyboard reachable — `focusRing` draws the ring the canvas never did.
 */
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { DfcSurface } from '../../../Utils/dfcSurface';
import { DFC_PALETTE, withAlpha } from '../../../Utils/dfcBrand';

export const omit = (...props: string[]) => ({
  shouldForwardProp: (prop: PropertyKey) => !props.includes(prop as string),
});

export interface SurfaceProp {
  surface: DfcSurface;
}

export const focusRing = (surface: DfcSurface) => `
  &:focus-visible {
    outline: 2px solid ${surface.dark ? DFC_PALETTE.green : DFC_PALETTE.black};
    outline-offset: 2px;
  }
`;

export const Eyebrow = styled(Typography, omit('tone'))<{ tone: string }>`
  && {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${({ tone }) => tone};
  }
`;

export const SectionLabel = styled(Eyebrow)`
  && {
    font-size: 11px;
    letter-spacing: 0.1em;
  }
`;

export const Body = styled(Typography, omit('surface'))<SurfaceProp>`
  && {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ surface }) => surface.ink2};
  }
`;

export const PillButton = styled(ButtonBase, omit('surface', 'variant'))<
  SurfaceProp & { variant: 'solid' | 'outline' }
>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 18px;
    border-radius: 999px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    background: ${({ surface, variant }) => (variant === 'solid' ? surface.ctaBg : 'transparent')};
    color: ${({ surface, variant }) => (variant === 'solid' ? surface.ctaFg : surface.ink)};
    border: ${({ surface, variant }) =>
      variant === 'solid' ? 'none' : `1px solid ${surface.border}`};
    ${({ surface }) => focusRing(surface)}

    &.Mui-disabled {
      opacity: 0.4;
    }
  }
`;

/** Underlined inline action ("See all 8 zones →"). Ink in light, green in dark — never blue. */
export const TextButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    font-size: 11px;
    font-weight: 700;
    font-family: inherit;
    color: ${({ surface }) => (surface.dark ? DFC_PALETTE.green : surface.ink)};
    text-decoration: underline;
    text-underline-offset: 3px;
    ${({ surface }) => focusRing(surface)}
  }
`;

/** 32px round control on the track colour — the day strip's week arrows. */
export const IconButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    background: ${({ surface }) => surface.track};
    color: ${({ surface }) => surface.ink};
    ${({ surface }) => focusRing(surface)}

    &.Mui-disabled {
      opacity: 0.35;
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

/** Centred muted block for empty and error states. */
export const StateBlock = styled(Box, omit('surface'))<SurfaceProp>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  text-align: center;
  color: ${({ surface }) => surface.ink3};
  font-size: 13px;
`;

export const NoteCard = styled(Box, omit('surface'))<SurfaceProp>`
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ surface }) => surface.soft};
  border: 1px solid ${({ surface }) => surface.border};
  font-size: 12px;
  line-height: 1.5;
  color: ${({ surface }) => surface.ink2};
`;

const shimmer = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`;

/** Surface-aware skeleton block with a light sweep; MUI's Skeleton is grey-on-white only. */
export const SkeletonBlock = styled(Box, omit('surface', 'h', 'w', 'r'))<
  SurfaceProp & { h: number; w?: string; r?: number }
>`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  height: ${({ h }) => h}px;
  width: ${({ w = '100%' }) => w};
  border-radius: ${({ r = 12 }) => r}px;
  background: ${({ surface }) => surface.track};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ surface }) => withAlpha(DFC_PALETTE.white, surface.dark ? 0.07 : 0.9)},
      transparent
    );
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
      display: none;
    }
  }
`;

export const Stack = styled(Box, omit('gap'))<{ gap: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap}px;
`;

export const Row = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
